// Dependencies
import PropTypes from "prop-types";
import Select from "react-select";
import React, { useState, useEffect } from "react";

// hooks
import useRequest from "../../../../../Hooks/useRequest";

// styles
import css from "./DeletedObjects.styl";
import DeletedReviewReports from "./Collections/ReviewReports";
import DeletedReviews from "./Collections/Reviews";
import { isValidObjectId } from "../../../../../Utils/isValidObjectId";

const COLLECTION_VALUES = {
	REVIEW_REPORTS: "review_report",
	REVIEWS: "review",
};

const options = [
	{ value: COLLECTION_VALUES.REVIEW_REPORTS, label: "Review Forms" },
	{ value: COLLECTION_VALUES.REVIEWS, label: "Reviews" },
];

/**
 * With the given filters, return the filters to be sent to the API. This
 * will remove an unset filter values.
 */
const selectFilters = (collection, state) => {
	const collectionFilters = state[collection.value];

	if (collection.value === COLLECTION_VALUES.REVIEW_REPORTS) {
		return {
			...(collectionFilters.id && { id: collectionFilters.id.trim() }),
			...(collectionFilters.reviewId && { reviewId: collectionFilters.reviewId.trim() }),
			...(collectionFilters.name && { formName: collectionFilters.name.trim() }),
			...(collectionFilters.subject && { subject: collectionFilters.subject.trim() }),
			...(collectionFilters.author && { author: collectionFilters.author.trim() }),
			...(collectionFilters.signer && { signer: collectionFilters.signer.trim() }),
			collection: collection.value,
		};
	} else if (collection.value === COLLECTION_VALUES.REVIEWS) {
		return {
			...(collectionFilters.id && { id: collectionFilters.id.trim() }),
			...(collectionFilters.name && { name: collectionFilters.name.trim() }),
			...(collectionFilters.reviewCycleName && { reviewCycleName: collectionFilters.reviewCycleName.trim() }),
			...(collectionFilters.subject && { subject: collectionFilters.subject.trim() }),
			collection: collection.value,
		};
	} else {
		return {};
	}
};

const DeletedObjects = (props) => {
	const { companyID } = props;

	/**
	 * Holds state of the filters
	 */
	const [collection, setCollection] = useState(options[0]);
	const defaultFilterState = {
		[COLLECTION_VALUES.REVIEW_REPORTS]: {
			id: "",
			reviewId: "",
			name: "",
			subject: "",
			author: "",
			signer: "",
		},
		[COLLECTION_VALUES.REVIEWS]: {
			id: "",
			name: "",
			reviewCycleName: "",
			subject: "",
		},
	};
	const [filterState, setFilterState] = useState(defaultFilterState);

	const [deletedObjectWarningModalState, setDeletedObjectWarningModalState] = useState({
		displayDeletedObjectWarningModal: false,
		message: "Nothing to see here",
	});

	const [deletedObjectConfirmationModalState, setDeletedObjectConfirmationModalState] = useState({
		displayDeletedObjectConfirmationModal: false,
		deletedObjectId: null,
	});

	const [tableData, setTableData] = useState([]);

	/**
	 * fetch objects from database with given filters
	 */
	const { data, revalidate, isRequesting } = useRequest(
		"RecentlyDeletedObjects",
		{ filters: { companyID, ...selectFilters(collection, filterState) }, skip: 0, limit: 100 },
		{ manual: true }
	);

	/**
	 * refetch data when filters change with debounce
	 */
	useEffect(() => {
		setTableData(data ? data.deletedObjectCollectionData.deletedObjects : []);
	}, [JSON.stringify(data)]);

	/**
	 * refetch data when collection changes
	 */
	useEffect(() => {
		revalidate();
	}, [collection]);

	/**
	 * function for parsing filters input before revalidating
	 */
	const revalidateSafely = (collection, filterState) => {
		const fieldsToValidate = {
			[COLLECTION_VALUES.REVIEW_REPORTS]: ["id", "reviewId"],
			[COLLECTION_VALUES.REVIEWS]: ["id"],
		};

		if (collection && filterState) {
			const fieldsToCheck = fieldsToValidate[collection.value] || [];

			const allFieldsValid = fieldsToCheck.every((field) => {
				const value = filterState[collection.value][field];
				return !value || isValidObjectId(value);
			});

			if (allFieldsValid) {
				revalidate();
			}
		}
	};

	/**
	 * refetch data when filters change with debounce
	 */
	useEffect(() => {
		const timer = setTimeout(() => {
			revalidateSafely(collection, filterState);
		}, 600);

		return () => {
			clearTimeout(timer);
		};
	}, [filterState]);

	/**
	 * Update Modal State when data changes
	 */
	useEffect(() => {
		setDeletedObjectWarningModalState({
			displayDeletedObjectWarningModal: data ? data.deletedObjectCollectionData.displayWarningModal : false,
			message: data ? data.deletedObjectCollectionData.warningModalMessage : "NA",
		});
	}, [data]);

	/**
	 * Handle input changes and update state
	 */
	const handleChange = (name, value) => {
		setFilterState((prevState) => ({
			...prevState,
			[collection.value]: {
				...prevState[collection.value],
				[name]: value,
			},
		}));
	};

	return (
		<div>
			<div className={css.filters}>
				<div className={css.filtersContainer}>
					<div className={css.filterBox}>
						<label>Collection</label>
						<div className={css.filterInput}>
							<Select value={collection} options={options} onChange={(value) => setCollection(value)} />
						</div>
					</div>
				</div>
			</div>
			{collection.value === COLLECTION_VALUES.REVIEW_REPORTS ? (
				<DeletedReviewReports
					companyID={companyID}
					filterState={filterState[COLLECTION_VALUES.REVIEW_REPORTS]}
					tableData={tableData}
					setTableData={setTableData}
					deletedObjectWarningModalState={deletedObjectWarningModalState}
					deletedObjectConfirmationModalState={deletedObjectConfirmationModalState}
					handleChange={handleChange}
					setDeletedObjectWarningModalState={setDeletedObjectWarningModalState}
					setDeletedObjectConfirmationModalState={setDeletedObjectConfirmationModalState}
					isLoading={isRequesting}
					data={data}
				/>
			) : collection.value === COLLECTION_VALUES.REVIEWS ? (
				<DeletedReviews
					companyId={companyID}
					filterState={filterState[COLLECTION_VALUES.REVIEWS]}
					handleChange={handleChange}
					isLoading={isRequesting}
					tableData={tableData}
					setTableData={setTableData}
					deletedObjectWarningModalState={deletedObjectWarningModalState}
					deletedObjectConfirmationModalState={deletedObjectConfirmationModalState}
					setDeletedObjectWarningModalState={setDeletedObjectWarningModalState}
					setDeletedObjectConfirmationModalState={setDeletedObjectConfirmationModalState}
				/>
			) : null}
		</div>
	);
};

DeletedObjects.propTypes = {
	companyID: PropTypes.string.isRequired,
};

export default DeletedObjects;
